import computeBulletBands, {
  computeBulletDomain,
  getBandEdges,
  getBulletNumbers,
  getBulletStatus,
} from 'components/BulletChart/computeBulletBands';
import { toHeatmapScale } from 'components/V5Gadget/chartPalette';
import { toGaugeColor } from 'components/StudioCharts/toChartModel/gauge';
import { ChartColors } from 'theme/tokens';

// Made-up fleet data for the palette's previews, as the chart models Studio
// draws real data with (see toChartModel), so a preview is drawn exactly as
// the chart it's of. In the default chart colours.

export const SAMPLE_METRICS: Record<
  string,
  { prefix?: string; suffix?: string }
> = {
  revenue: { prefix: '$' },
  loads: {},
  rpm: { prefix: '$' },
  onTime: { suffix: '%' },
  miles: {},
  csa: {},
};

// CSA percentiles against their FMCSA thresholds, where lower is better
const CSA_BULLETS: BulletCharts.Row[] = [
  { label: 'Unsafe Driving', value: 41, thresholds: [55, 65] },
  { label: 'HOS Compliance', value: 62, thresholds: [55, 65] },
  { label: 'Vehicle Maint.', value: 84, thresholds: [70, 80] },
].map(({ label, value, thresholds }) => {
  const edges = getBandEdges({
    bands: { mode: 'absolute', thresholds: [thresholds[0], thresholds[1]] },
    lowerIsBetter: true,
  });
  const target = thresholds[1];
  const domain = computeBulletDomain({
    numbers: getBulletNumbers({ value, target, edges }),
    maxY: 100,
  });
  return {
    key: label,
    label,
    metricId: 'csa',
    value,
    target,
    bands: computeBulletBands({ domain, edges, lowerIsBetter: true }),
    domain,
    status: getBulletStatus({ value, edges, lowerIsBetter: true }),
    lowerIsBetter: true,
  };
});

const color = (index: number) => ChartColors[index % ChartColors.length];

const toCategories = (labels: string[]): StudioCharts.Category[] =>
  labels.map((label) => ({ key: label, label, title: label }));

const MONTHS = toCategories(['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun']);
const REGIONS = toCategories(['North', 'South', 'East', 'West']);

const series = ({
  id,
  metricId,
  name,
  kind,
  values,
  colorIndex,
  axisIndex = 0,
}: {
  id: string;
  metricId: string;
  name: string;
  kind: StudioCharts.SeriesKind;
  values: number[];
  colorIndex: number;
  axisIndex?: number;
}): StudioCharts.Series => ({
  id,
  key: name,
  metricId,
  metricIds: [metricId],
  name,
  kind,
  role: 'value',
  values,
  color: color(colorIndex),
  axisIndex,
});

const cartesian = (
  model: Pick<StudioCharts.CartesianModel, 'categories' | 'series'> &
    Partial<StudioCharts.CartesianModel>,
): StudioCharts.CartesianModel => ({
  kind: 'cartesian',
  isHorizontal: false,
  valueAxes: [{}],
  showLegend: model.series.length > 1,
  isNoData: false,
  ...model,
});

const parts = (
  model: Pick<StudioCharts.PartsModel, 'kind' | 'parts'> &
    Partial<StudioCharts.PartsModel>,
): StudioCharts.PartsModel => ({
  seriesId: 'loads',
  metricId: 'loads',
  metricIds: ['loads'],
  name: 'Loads',
  showLegend: false,
  isNoData: false,
  ...model,
});

const toParts = (
  values: [string, number][],
  { colorOffset = 0 }: { colorOffset?: number } = {},
): StudioCharts.Part[] =>
  values.map(([name, value], index) => ({
    key: name,
    name,
    value,
    color: color(index + colorOffset),
  }));

export interface SampleChart {
  model: StudioCharts.Model;
  style?: Studio.ChartStyle;
}

// The sample a vis type's preview draws, or undefined for those that aren't
// charts (see SampleCards)
export const getSampleChart = ({
  visType,
  theme,
}: {
  visType: Studio.VisType;
  theme: StudioCharts.Theme;
}): SampleChart | undefined => {
  switch (visType) {
    case 'line':
      return {
        model: cartesian({
          categories: MONTHS,
          isDateAxis: true,
          series: [
            series({
              id: 'loads',
              metricId: 'loads',
              name: 'Loads',
              kind: 'line',
              values: [420, 468, 451, 512, 538, 590],
              colorIndex: 0,
            }),
            series({
              id: 'loadsLastYear',
              metricId: 'loads',
              name: 'Last year',
              kind: 'line',
              values: [380, 402, 430, 421, 466, 488],
              colorIndex: 1,
            }),
          ],
        }),
        style: { curve: 'smooth' },
      };
    case 'bar':
      return {
        model: cartesian({
          categories: REGIONS,
          series: [
            series({
              id: 'loads',
              metricId: 'loads',
              name: 'Loads',
              kind: 'bar',
              values: [312, 468, 254, 391],
              colorIndex: 0,
            }),
          ],
        }),
      };
    case 'pictorialBar':
      return {
        model: cartesian({
          categories: toCategories(['North', 'South', 'East']),
          // A row of trucks per region
          isHorizontal: true,
          series: [
            series({
              id: 'trucks',
              metricId: 'loads',
              name: 'Trucks',
              kind: 'bar',
              values: [6, 9, 4],
              colorIndex: 2,
            }),
          ],
        }),
        style: { pictorial: { icon: 'truck' } },
      };
    case 'combo':
      return {
        model: cartesian({
          categories: MONTHS,
          isDateAxis: true,
          valueAxes: [{}, { isOpposite: true }],
          series: [
            series({
              id: 'revenue',
              metricId: 'revenue',
              name: 'Revenue',
              kind: 'bar',
              values: [182, 204, 196, 231, 248, 266],
              colorIndex: 0,
            }),
            series({
              id: 'rpm',
              metricId: 'rpm',
              name: 'Rate per mile',
              kind: 'line',
              values: [2.41, 2.38, 2.52, 2.6, 2.57, 2.71],
              colorIndex: 1,
              axisIndex: 1,
            }),
          ],
        }),
      };
    case 'stackedBar':
      return {
        model: cartesian({
          categories: toCategories(['North', 'South', 'West']),
          isHorizontal: true,
          stack: { isPercentage: false },
          drillsOn: 'series',
          series: [
            ['Dry van', [140, 180, 152]],
            ['Reefer', [88, 120, 64]],
            ['Flatbed', [52, 74, 92]],
          ].map(([name, values], index) =>
            series({
              id: `loads:${name}`,
              metricId: 'loads',
              name: name as string,
              kind: 'bar',
              values: values as number[],
              colorIndex: index,
            }),
          ),
        }),
      };
    case 'area':
      return {
        model: cartesian({
          categories: MONTHS,
          isDateAxis: true,
          stack: { isPercentage: false },
          drillsOn: 'series',
          series: [
            ['Dry van', [140, 162, 158, 181, 196, 210]],
            ['Reefer', [88, 92, 104, 110, 106, 124]],
            ['Flatbed', [52, 60, 58, 71, 80, 84]],
          ].map(([name, values], index) =>
            series({
              id: `loads:${name}`,
              metricId: 'loads',
              name: name as string,
              kind: 'area',
              values: values as number[],
              colorIndex: index,
            }),
          ),
        }),
      };
    case 'pie':
      return {
        model: parts({
          kind: 'pie',
          hole: 0.5,
          sharePrecision: 0,
          showLegend: true,
          parts: toParts([
            ['Van', 46],
            ['Reefer', 27],
            ['Flatbed', 17],
            ['Tanker', 10],
          ]),
        }),
      };
    case 'funnel':
      return {
        model: parts({
          kind: 'funnel',
          parts: toParts([
            ['Quoted', 1240],
            ['Tendered', 860],
            ['Booked', 610],
            ['Delivered', 548],
          ]),
        }),
      };
    case 'treeMap':
      return {
        model: parts({
          kind: 'treemap',
          showValues: true,
          parts: toParts([
            ['Texas', 412],
            ['California', 318],
            ['Ohio', 196],
            ['Georgia', 164],
            ['Illinois', 128],
            ['Arizona', 86],
          ]),
        }),
      };
    case 'packedBubble':
      return {
        model: parts({
          kind: 'packedBubble',
          parts: toParts([
            ['Acme', 420],
            ['Globex', 330],
            ['Initech', 260],
            ['Hooli', 210],
            ['Stark', 170],
          ]),
        }),
      };
    case 'radar':
      return {
        model: {
          kind: 'polar',
          // The room a legend takes would crowd its labels
          showLegend: false,
          isNoData: false,
          categories: toCategories([
            'Safety',
            'On time',
            'Fuel',
            'Idle',
            'Utilisation',
          ]),
          series: [
            series({
              id: 'teamA',
              metricId: 'onTime',
              name: 'Team A',
              kind: 'line',
              values: [92, 78, 64, 81, 70],
              colorIndex: 0,
            }),
            series({
              id: 'teamB',
              metricId: 'onTime',
              name: 'Team B',
              kind: 'line',
              values: [70, 88, 82, 60, 86],
              colorIndex: 1,
            }),
          ],
        },
      };
    case 'scatter':
      return {
        model: {
          kind: 'scatter',
          seriesId: 'drivers',
          metricIds: ['miles', 'revenue'],
          name: 'Drivers',
          showLegend: false,
          isNoData: false,
          color: color(2),
          x: { metricId: 'miles', name: 'Miles' },
          y: { metricId: 'revenue', name: 'Revenue' },
          points: [
            [42000, 91],
            [51000, 114],
            [36000, 72],
            [64000, 138],
            [58000, 121],
            [29000, 64],
            [71000, 146],
            [47000, 98],
            [61000, 119],
            [33000, 79],
            [54000, 102],
            [69000, 151],
          ].map(([x, y], index) => ({
            key: `driver-${index}`,
            name: `Driver ${index + 1}`,
            x,
            y,
          })),
        },
      };
    case 'heatmap': {
      const columns = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
      const rows = ['Night', 'Evening', 'Afternoon', 'Morning'];
      const values = [
        [12, 18, 15, 20, 26],
        [34, 41, 38, 45, 52],
        [58, 66, 61, 72, 80],
        [44, 52, 49, 57, 63],
      ];
      return {
        model: {
          kind: 'heatmap',
          seriesId: 'loads',
          metricId: 'loads',
          metricIds: ['loads'],
          name: 'Loads',
          showLegend: false,
          isNoData: false,
          columns,
          rows,
          cells: values.flatMap((row, rowIndex) =>
            row.map((value, column) => ({ column, row: rowIndex, value })),
          ),
          ...toHeatmapScale(ChartColors),
        },
      };
    }
    case 'waterfall':
      return {
        model: {
          kind: 'waterfall',
          seriesId: 'revenue',
          metricId: 'revenue',
          metricIds: ['revenue'],
          name: 'Revenue',
          showLegend: false,
          isNoData: false,
          showValues: false,
          upColor: ChartColors[0],
          downColor: ChartColors[1],
          steps: [
            { key: 'Haul', label: 'Haul', value: 180, isTotal: false },
            { key: 'Fuel', label: 'Fuel', value: 42, isTotal: false },
            { key: 'Claims', label: 'Claims', value: -24, isTotal: false },
            { key: 'Tolls', label: 'Tolls', value: -16, isTotal: false },
            { label: 'Total', value: 182, isTotal: true },
          ],
        },
      };
    case 'sankey': {
      const nodes: StudioCharts.SankeyNode[] = [
        ['north', 'North', true],
        ['south', 'South', true],
        ['dryVan', 'Dry van', false],
        ['reefer', 'Reefer', false],
        ['flatbed', 'Flatbed', false],
      ].map(([id, name, isSource], index) => ({
        id: id as string,
        name: name as string,
        isSource: isSource as boolean,
        color: color(index),
      }));
      return {
        model: {
          kind: 'sankey',
          seriesId: 'loads',
          metricId: 'loads',
          metricIds: ['loads'],
          name: 'Loads',
          showLegend: false,
          isNoData: false,
          nodes,
          links: [
            { from: 'north', to: 'dryVan', weight: 140 },
            { from: 'north', to: 'reefer', weight: 60 },
            { from: 'north', to: 'flatbed', weight: 40 },
            { from: 'south', to: 'dryVan', weight: 90 },
            { from: 'south', to: 'reefer', weight: 110 },
            { from: 'south', to: 'flatbed', weight: 30 },
          ],
        },
      };
    }
    case 'gauge': {
      const fill = 72;
      return {
        model: {
          kind: 'gauge',
          seriesId: 'onTime',
          metricId: 'onTime',
          name: 'On time',
          showLegend: false,
          isNoData: false,
          fill,
          color: toGaugeColor({ fill, theme }),
          valueLabel: '94%',
          startLabel: '81%',
          endLabel: '98%',
        },
      };
    }
    case 'bullet':
      return {
        model: {
          kind: 'bullet',
          seriesId: 'csa',
          metricId: 'csa',
          metricIds: ['csa'],
          rows: CSA_BULLETS,
          minFontSize: 11,
          maxFontSize: 14,
          display: {},
          showLegend: false,
          isNoData: false,
        },
      };
    case 'regionMap':
      return {
        model: {
          kind: 'regionMap',
          area: 'us',
          drawAs: 'fill',
          seriesId: 'loads',
          metricId: 'loads',
          metricIds: ['loads'],
          name: 'Loads',
          showLegend: false,
          isNoData: false,
          color: color(2),
          unmatched: [],
          regions: [
            ['US-TX', 'Texas', 412],
            ['US-CA', 'California', 318],
            ['US-OH', 'Ohio', 196],
            ['US-GA', 'Georgia', 164],
            ['US-IL', 'Illinois', 128],
            ['US-AZ', 'Arizona', 86],
            ['US-PA', 'Pennsylvania', 142],
            ['US-FL', 'Florida', 176],
            ['US-NC', 'North Carolina', 98],
            ['US-WA', 'Washington', 74],
            ['US-CO', 'Colorado', 62],
            ['US-TN', 'Tennessee', 110],
          ].map(([id, name, value]) => ({
            id: id as string,
            name: name as string,
            key: (id as string).slice(3),
            value: value as number,
          })),
        },
      };
    default:
      return undefined;
  }
};
